import { useCallback, useMemo } from 'react'
import log from 'loglevel'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { GetComplianceFrameworksRequest } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { CreateCustomFrameworkRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import {
  BulkCreateFrameworkRequirementsRequest,
  CreateOrUpdateFrameworkRequest,
  DeleteFrameworkRequirementsRequest,
  FrameworkRequirementInput as FrameworkRequirementInputProto,
  FrameworkSection,
  SaveFrameworkSectionsRequest,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import {
  Control,
  ListControlsRequest,
  UpdateControlFrameworksRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useFrameworks } from 'src/components/async/Framework/useFrameworks'
import { useFrameworkSections } from 'src/components/async/Framework/useFrameworkSections'
import {
  useControls,
  useInvalidateControlsCache,
} from 'src/components/async/model/control/useControls'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { useInvalidateComplianceFrameworksCache } from 'src/components/async/complianceframework/useComplianceFrameworks'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import {
  RequirementDraft,
  SectionDraft,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'

const useInvalidateFrameworkCaches = (): (() => Promise<void>) => {
  const invalidateComplianceFrameworks =
    useInvalidateComplianceFrameworksCache()
  const invalidateControls = useInvalidateControlsCache()
  const revalidateByMethod = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      invalidateComplianceFrameworks(),
      invalidateControls(),
      revalidateByMethod(ModelPromiseClient.prototype.listFrameworks),
      revalidateByMethod(ModelPromiseClient.prototype.listFrameworkSections),
    ])
  }, [invalidateComplianceFrameworks, invalidateControls, revalidateByMethod])
}

const assertNoDuplicateSectionNames = (
  existingNames: string[],
  incoming: { name: string }[],
): void => {
  const existingNormalized = new Set(
    existingNames.map((n) => normalizeSectionName(n)),
  )
  const duplicates = new Set<string>()
  const seen = new Set<string>()
  for (const section of incoming) {
    const normalized = normalizeSectionName(section.name)
    if (existingNormalized.has(normalized) || seen.has(normalized)) {
      duplicates.add(section.name)
    }
    seen.add(normalized)
  }
  if (duplicates.size > 0) {
    throw new DuplicateSectionNameError(Array.from(duplicates))
  }
}

/**
 * Loads the account's controls and pre-derives the two shapes the framework
 * modals consume: a SelectItem[] for dropdowns and a Set<modelId> for CSV
 * mapping validation.
 */
export const useFrameworkControls = (
  enabled: boolean,
): {
  controlOptions: SelectItem[]
  validControlIdSet: Set<string>
  controls: Control[]
  isLoading: boolean
} => {
  const { data: controlsData, isLoading } = useControls(
    new ListControlsRequest(),
    enabled,
  )
  const controls = useMemo(
    () => controlsData?.getItemsList() ?? [],
    [controlsData],
  )
  const controlOptions = useMemo<SelectItem[]>(
    () =>
      controls.map((c) => ({
        name: `${c.getModelId()} ${c.getName()}`,
        value: c.getModelId(),
      })),
    [controls],
  )
  const validControlIdSet = useMemo(
    () => new Set(controls.map((c) => c.getModelId())),
    [controls],
  )
  return { controlOptions, validControlIdSet, controls, isLoading }
}

/**
 * Loads existing section + requirement names for a framework, plus the raw
 * sections response (callers need it for `existingSections` arg to save hooks).
 */
export const useExistingFrameworkNames = (
  framework: ComplianceFramework,
  show: boolean,
): {
  existingSectionNames: string[]
  existingRequirementNames: string[]
  sectionsData: ReturnType<typeof useFrameworkSections>['data']
  isLoadingSections: boolean
} => {
  const cfModelId = framework.getModelId()
  const cfRecordId = framework.getId()
  const { data: sectionsData, isLoading: isLoadingSections } =
    useFrameworkSections(cfModelId, show)
  const { data: frameworksData } = useFrameworks()

  const existingSectionNames = useMemo(
    () => (sectionsData?.getSectionsList() ?? []).map((s) => s.getName()),
    [sectionsData],
  )
  const existingRequirementNames = useMemo(
    () =>
      (frameworksData?.getItemsList() ?? [])
        .filter(
          (f) =>
            f.getComplianceFrameworkId() === cfRecordId ||
            f.getComplianceFrameworkId() === cfModelId,
        )
        .map((f) => f.getName()),
    [frameworksData, cfRecordId, cfModelId],
  )

  return {
    existingSectionNames,
    existingRequirementNames,
    sectionsData,
    isLoadingSections,
  }
}

export const useSearchableFrameworks = (
  frameworks: ComplianceFramework[],
): SearchableData<ComplianceFramework>[] =>
  useMemo(
    () =>
      prepareSearchableData(
        frameworks,
        (framework: ComplianceFramework) => framework.getId(),
        (framework: ComplianceFramework) => [
          framework.getName(),
          framework.getDescription(),
        ],
      ),
    [frameworks],
  )

type FrameworkMetadataCounts = {
  requirementsByFrameworkId: Record<string, number>
  controlsByFrameworkId: Record<string, number>
  auditsByFrameworkId: Record<string, number>
  isLoading: boolean
}

export const useFrameworkMetadataCounts = (): FrameworkMetadataCounts => {
  const { data: frameworksData, isLoading: frameworksLoading } = useFrameworks()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    undefined,
    true,
    undefined,
    undefined,
    false,
    true,
  )
  const { data: auditsData, isLoading: auditsLoading } = useAudits()

  const requirementsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    frameworksData?.getItemsList().forEach((framework) => {
      const cfId = framework.getComplianceFrameworkId()
      if (cfId) counts[cfId] = (counts[cfId] ?? 0) + 1
    })
    return counts
  }, [frameworksData])

  const controlsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    controlsData?.getItemsList().forEach((control) => {
      control.getComplianceFrameworkIdsList().forEach((cfId) => {
        counts[cfId] = (counts[cfId] ?? 0) + 1
      })
    })
    return counts
  }, [controlsData])

  const auditsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    auditsData?.getItemsList().forEach((audit) => {
      audit.getComplianceFrameworksList().forEach((cf) => {
        const cfId = cf.getId()
        if (cfId) counts[cfId] = (counts[cfId] ?? 0) + 1
      })
    })
    return counts
  }, [auditsData])

  return {
    requirementsByFrameworkId,
    controlsByFrameworkId,
    auditsByFrameworkId,
    isLoading: frameworksLoading || controlsLoading || auditsLoading,
  }
}

export type CreateCustomFrameworkWithSectionsArgs = {
  name: string
  description?: string
  sections: SectionDraft[]
}

export type AddFrameworkSectionsArgs = {
  complianceFrameworkModelId: string
  existingSections: FrameworkSection[]
  sections: SectionDraft[]
}

export class DuplicateSectionNameError extends Error {
  duplicateSectionNames: string[]

  constructor(duplicateSectionNames: string[]) {
    super(
      `Duplicate section names: ${duplicateSectionNames
        .map((name) => `"${name}"`)
        .join(', ')}`,
    )
    this.name = 'DuplicateSectionNameError'
    this.duplicateSectionNames = duplicateSectionNames
  }
}

const normalizeSectionName = (name: string): string => name.trim().toLowerCase()

export const useCreateCustomFrameworkWithSections = (): ((
  args: CreateCustomFrameworkWithSectionsArgs,
) => Promise<{ complianceFrameworkRecordId: string }>) => {
  const accountClient = useAuthorizedGrpcClient(AccountPromiseClient)
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const auditClient = useAuthorizedGrpcClient(AuditPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({
      name,
      description,
      sections,
    }): Promise<{ complianceFrameworkRecordId: string }> => {
      const response = await accountClient.createCustomFramework(
        new CreateCustomFrameworkRequest()
          .setName(name)
          .setDescription(description ?? ''),
      )
      const complianceFrameworkModelId = response.getModelId()

      const sectionsWithNames = sections.filter((s) => s.name.trim())
      const groupTempIdToModelId = new Map<string, string>()

      if (sectionsWithNames.length > 0) {
        const saveReq = new SaveFrameworkSectionsRequest()
          .setComplianceFrameworkModelId(complianceFrameworkModelId)
          .setSectionsList(
            sectionsWithNames.map((s) => {
              const row = new FrameworkSection()
                .setName(s.name.trim())
                .setDetails(s.description.trim())
              if (s.modelId) {
                row.setModelId(s.modelId)
              }
              return row
            }),
          )
        const saveResp = await modelClient.saveFrameworkSections(saveReq)
        const savedRows = saveResp.getSectionsList()
        sectionsWithNames.forEach((s, idx) => {
          const modelId = savedRows[idx]?.getModelId() ?? ''
          if (modelId) groupTempIdToModelId.set(s.tempId, modelId)
        })
      }

      const requirementInputs = sectionsWithNames.flatMap((section) =>
        section.requirements
          .filter((r) => r.name.trim())
          .map((r) => {
            const input = new FrameworkRequirementInputProto()
              .setName(r.name.trim())
              .setDescription(r.description.trim())
              .setControlIdsList(r.controlIds)
            const groupModelId = groupTempIdToModelId.get(section.tempId) ?? ''
            if (groupModelId) {
              input.setGroup(groupModelId)
            }
            return input
          }),
      )

      if (requirementInputs.length > 0) {
        const bulkRequest = new BulkCreateFrameworkRequirementsRequest()
          .setComplianceFrameworkModelId(complianceFrameworkModelId)
          .setRequirementsList(requirementInputs)
        await modelClient.bulkCreateFrameworkRequirements(bulkRequest)
      }

      await invalidateFrameworkCaches()

      const refreshed = await auditClient.getComplianceFrameworks(
        new GetComplianceFrameworksRequest(),
      )
      const recordId = refreshed
        .getItemsList()
        .find((f) => f.getModelId() === complianceFrameworkModelId)
        ?.getId()

      if (!recordId) {
        log.error(
          'Created framework but could not resolve record id for navigation',
          { complianceFrameworkModelId },
        )
        throw new Error(
          'Framework was created but could not be loaded. Please refresh.',
        )
      }

      return { complianceFrameworkRecordId: recordId }
    },
    [accountClient, auditClient, invalidateFrameworkCaches, modelClient],
  )
}

export const useAddFrameworkSections = (): ((
  args: AddFrameworkSectionsArgs,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({
      complianceFrameworkModelId,
      existingSections,
      sections,
    }: AddFrameworkSectionsArgs): Promise<void> => {
      const sectionsWithNames = sections
        .map((section) => ({
          ...section,
          name: section.name.trim(),
          description: section.description.trim(),
          requirements: section.requirements.map((requirement) => ({
            ...requirement,
            name: requirement.name.trim(),
            description: requirement.description.trim(),
          })),
        }))
        .filter((section) => section.name.length > 0)

      if (sectionsWithNames.length === 0) return

      assertNoDuplicateSectionNames(
        existingSections.map((section) => section.getName()),
        sectionsWithNames,
      )

      const saveReq = new SaveFrameworkSectionsRequest()
        .setComplianceFrameworkModelId(complianceFrameworkModelId)
        .setSectionsList([
          ...existingSections.map((section) =>
            new FrameworkSection()
              .setModelId(section.getModelId())
              .setName(section.getName().trim())
              .setDetails(section.getDetails().trim()),
          ),
          ...sectionsWithNames.map((section) =>
            new FrameworkSection()
              .setName(section.name)
              .setDetails(section.description),
          ),
        ])
      const saveResp = await modelClient.saveFrameworkSections(saveReq)
      const savedSections = saveResp.getSectionsList()

      const existingSectionCount = existingSections.length
      const sectionTempIdToGroupId = new Map<string, string>()
      sectionsWithNames.forEach((section, index) => {
        const groupModelId =
          savedSections[existingSectionCount + index]?.getModelId()
        if (groupModelId) {
          sectionTempIdToGroupId.set(section.tempId, groupModelId)
        }
      })

      const requirements = sectionsWithNames.flatMap((section) =>
        section.requirements
          .filter((requirement) => requirement.name.length > 0)
          .map((requirement) => {
            const input = new FrameworkRequirementInputProto()
              .setName(requirement.name)
              .setDescription(requirement.description)
              .setControlIdsList(requirement.controlIds)
            const groupModelId =
              sectionTempIdToGroupId.get(section.tempId) ?? ''
            if (groupModelId) input.setGroup(groupModelId)
            return input
          }),
      )

      if (requirements.length > 0) {
        await modelClient.bulkCreateFrameworkRequirements(
          new BulkCreateFrameworkRequirementsRequest()
            .setComplianceFrameworkModelId(complianceFrameworkModelId)
            .setRequirementsList(requirements),
        )
      }

      await invalidateFrameworkCaches()
    },
    [invalidateFrameworkCaches, modelClient],
  )
}

export const useDeleteFrameworkRequirements = (): ((
  requirementModelIds: string[],
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async (requirementModelIds: string[]): Promise<void> => {
      if (requirementModelIds.length === 0) return

      await modelClient.deleteFrameworkRequirements(
        new DeleteFrameworkRequirementsRequest().setRequirementModelIdsList(
          requirementModelIds,
        ),
      )

      await invalidateFrameworkCaches()
    },
    [invalidateFrameworkCaches, modelClient],
  )
}

export type SaveFrameworkSectionsArgs = {
  complianceFrameworkModelId: string
  /** Snapshot of the framework's persisted state at the time the editor was opened. */
  originalSections: SectionDraft[]
  /** Edited state from the editor. */
  newSections: SectionDraft[]
  /** Full controls list (from useControls) — needed to look up row ids and merge framework_ids. */
  controls: Control[]
}

type FlatRequirement = {
  draft: RequirementDraft
  sectionModelId: string
  sectionTempId: string
}

/**
 * Full desired-set submit for a framework's sections + requirements. Handles:
 *   - Section create / update / delete (via SaveFrameworkSections which is upsert-with-delete-missing)
 *   - Requirement create (BulkCreateFrameworkRequirements — also wires control links)
 *   - Requirement update (CreateOrUpdateFramework per changed row)
 *   - Requirement delete (DeleteFrameworkRequirements — also cleans up control links)
 *   - Control-mapping changes on updated requirements (UpdateControlFrameworks with merged set per affected control)
 */
export const useSaveFrameworkSections = (): ((
  args: SaveFrameworkSectionsArgs,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const controlClient = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({
      complianceFrameworkModelId,
      originalSections,
      newSections,
      controls,
    }): Promise<void> => {
      const desiredSections = newSections.filter((s) => s.name.trim())

      const saveReq = new SaveFrameworkSectionsRequest()
        .setComplianceFrameworkModelId(complianceFrameworkModelId)
        .setSectionsList(
          desiredSections.map((s) => {
            const g = new FrameworkSection()
              .setName(s.name.trim())
              .setDetails(s.description.trim())
            if (s.modelId) g.setModelId(s.modelId)
            return g
          }),
        )
      const saveResp = await modelClient.saveFrameworkSections(saveReq)
      const savedSections = saveResp.getSectionsList()

      const sectionTempIdToModelId = new Map<string, string>()
      desiredSections.forEach((s, idx) => {
        const saved = savedSections[idx]
        if (saved) sectionTempIdToModelId.set(s.tempId, saved.getModelId())
      })

      const originalByModelId = new Map<
        string,
        { draft: RequirementDraft; sectionModelId?: string }
      >()
      for (const s of originalSections) {
        for (const r of s.requirements) {
          if (r.modelId) {
            originalByModelId.set(r.modelId, {
              draft: r,
              sectionModelId: s.modelId,
            })
          }
        }
      }

      const newFlat: FlatRequirement[] = []
      for (const s of desiredSections) {
        const sectionModelId = sectionTempIdToModelId.get(s.tempId) ?? ''
        for (const r of s.requirements) {
          if (!r.name.trim()) continue
          newFlat.push({
            draft: r,
            sectionModelId,
            sectionTempId: s.tempId,
          })
        }
      }

      const newReqModelIdSet = new Set(
        newFlat.map((x) => x.draft.modelId).filter((v): v is string => !!v),
      )

      const toDelete: string[] = []
      for (const modelId of originalByModelId.keys()) {
        if (!newReqModelIdSet.has(modelId)) toDelete.push(modelId)
      }

      const toCreate: FlatRequirement[] = []
      const toUpdate: FlatRequirement[] = []
      for (const nr of newFlat) {
        if (!nr.draft.modelId) {
          toCreate.push(nr)
          continue
        }
        const orig = originalByModelId.get(nr.draft.modelId)
        if (!orig) continue
        const nameChanged = orig.draft.name.trim() !== nr.draft.name.trim()
        const descChanged =
          orig.draft.description.trim() !== nr.draft.description.trim()
        const groupChanged = (orig.sectionModelId ?? '') !== nr.sectionModelId
        if (nameChanged || descChanged || groupChanged) {
          toUpdate.push(nr)
        }
      }

      if (toDelete.length > 0) {
        await modelClient.deleteFrameworkRequirements(
          new DeleteFrameworkRequirementsRequest().setRequirementModelIdsList(
            toDelete,
          ),
        )
      }

      if (toCreate.length > 0) {
        const bulkReq = new BulkCreateFrameworkRequirementsRequest()
          .setComplianceFrameworkModelId(complianceFrameworkModelId)
          .setRequirementsList(
            toCreate.map((c) => {
              const input = new FrameworkRequirementInputProto()
                .setName(c.draft.name.trim())
                .setDescription(c.draft.description.trim())
                .setControlIdsList(c.draft.controlIds)
              if (c.sectionModelId) input.setGroup(c.sectionModelId)
              return input
            }),
          )
        await modelClient.bulkCreateFrameworkRequirements(bulkReq)
      }

      for (const u of toUpdate) {
        const req = new CreateOrUpdateFrameworkRequest().setModelId(
          u.draft.modelId as string,
        )
        req.setName(new StringValue().setValue(u.draft.name.trim()))
        req.setDescription(
          new StringValue().setValue(u.draft.description.trim()),
        )
        if (u.sectionModelId) {
          req.setGroup(new StringValue().setValue(u.sectionModelId))
        }
        await modelClient.createOrUpdateFramework(req)
      }

      type ControlDelta = { add: Set<string>; remove: Set<string> }
      const deltaByControlModelId = new Map<string, ControlDelta>()
      const ensureDelta = (ctrlModelId: string): ControlDelta => {
        let d = deltaByControlModelId.get(ctrlModelId)
        if (!d) {
          d = { add: new Set(), remove: new Set() }
          deltaByControlModelId.set(ctrlModelId, d)
        }
        return d
      }
      for (const nr of newFlat) {
        if (!nr.draft.modelId) continue
        const orig = originalByModelId.get(nr.draft.modelId)
        if (!orig) continue
        const oldCtrls = new Set(orig.draft.controlIds)
        const newCtrls = new Set(nr.draft.controlIds)
        for (const c of newCtrls) {
          if (!oldCtrls.has(c)) ensureDelta(c).add.add(nr.draft.modelId)
        }
        for (const c of oldCtrls) {
          if (!newCtrls.has(c)) ensureDelta(c).remove.add(nr.draft.modelId)
        }
      }

      if (deltaByControlModelId.size > 0) {
        const controlByModelId = new Map<string, Control>()
        for (const c of controls) {
          if (deltaByControlModelId.has(c.getModelId())) {
            controlByModelId.set(c.getModelId(), c)
          }
        }

        await Promise.all(
          Array.from(deltaByControlModelId.entries()).map(
            async ([ctrlModelId, delta]) => {
              if (delta.add.size === 0 && delta.remove.size === 0) return
              const control = controlByModelId.get(ctrlModelId)
              if (!control) return
              const merged = new Set(control.getFrameworkIdsList())
              for (const a of delta.add) merged.add(a)
              for (const r of delta.remove) merged.delete(r)
              const req = new UpdateControlFrameworksRequest()
                .setControlId(control.getId())
                .setFrameworkIdsList(Array.from(merged))
                .setComplianceFrameworkIdsList(
                  control.getComplianceFrameworkIdsList(),
                )
              await controlClient.updateControlFrameworks(req)
            },
          ),
        )
      }

      await invalidateFrameworkCaches()
    },
    [controlClient, invalidateFrameworkCaches, modelClient],
  )
}
